import React from 'react';

import { css } from '@emotion/css';
import {
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
  Bar,
  ReferenceLine,
  Cell,
  CartesianGrid,
  ResponsiveContainer,
  ComposedChart,
  ReferenceLineProps,
  TooltipPayload,
  XAxisProps,
  YAxisProps,
} from 'recharts';
import { theme } from 'theme';

import { InventoryBarChartBar } from 'pages/purchasing/PurchasingDashboardPage/InventoryBarChart';

import { FlexSpace } from 'components/FlexSpace';
import { Typography } from 'components/Typography';

export interface NumberBarChartProps {
  data: { [key: string]: number; number: number }[];
  bars: InventoryBarChartBar[];
  valueFormatter: (value: number) => string;
  tooltipTitle: (payload: TooltipPayload['payload']) => React.ReactNode;
  referenceLines: ReferenceLineProps[];
  legend: React.ReactNode;
  customXAxisProps: XAxisProps;
  customYAxisProps: YAxisProps;
}
export const NumberBarChart = ({
  data = [],
  bars = [],
  valueFormatter,
  tooltipTitle,
  legend,
  referenceLines = [],
  customXAxisProps,
  customYAxisProps,
}: NumberBarChartProps) => (
  <div style={{ position: 'relative', width: '100%', height: '450px' }}>
    <ResponsiveContainer width="100%" height={450}>
      <ComposedChart data={data} height={450} stackOffset="sign" margin={{ bottom: 40, left: 20, right: 20, top: 20 }}>
        <XAxis dataKey="number" type="number" {...customXAxisProps} />
        <YAxis allowDataOverflow type="number" {...customYAxisProps} />
        <Tooltip
          content={(e: TooltipProps) => {
            if (e.active && e.payload && e.payload.length) {
              const { payload } = e.payload[0];
              return (
                <div
                  className={css`
                    background-color: rgba(255, 255, 255, 0.9);
                    font-weight: 500;
                    border-radius: 8px;
                    border: 2px solid ${theme.colors.neutral[400]};
                  `}
                >
                  <div
                    className={css`
                      padding: 6px;
                    `}
                  >
                    {tooltipTitle && (
                      <Typography style={{ fontWeight: 500 }}>{tooltipTitle(e.payload[0].payload)}</Typography>
                    )}
                    {bars.map((bar) => (
                      <FlexSpace key={bar.key}>
                        <div
                          className={css`
                            width: 16px;
                            height: 16px;
                            background-color: ${bar.color};
                            border-radius: 8px;
                          `}
                        />
                        <Typography>
                          {bar.title || bar.key}:{' '}
                          {bar.valueFormatter
                            ? bar.valueFormatter(payload[bar.key] || 0)
                            : valueFormatter(payload[bar.key] || 0)}
                        </Typography>
                      </FlexSpace>
                    ))}
                  </div>
                </div>
              );
            }

            return null;
          }}
        />
        <CartesianGrid strokeDasharray="3 3" />
        {bars.map((bar) => (
          <Bar key={bar.key} dataKey={bar.key} stackId="stack" fill={bar.color}>
            {data.map((d, index) => (
              <Cell key={`${bar.key}-${index}`} fill={bar.color} strokeWidth={5} stroke="5px solid black" />
            ))}
          </Bar>
        ))}
        {referenceLines.map((line, index) => (
          <ReferenceLine
            key={index}
            x={line.x}
            y={line.y}
            stroke={line.stroke}
            strokeWidth={line.strokeWidth || 1}
            strokeDasharray={line.strokeDasharray}
            isFront
            overflow="visible"
            opacity={100}
          />
        ))}
      </ComposedChart>
    </ResponsiveContainer>
    {legend && (
      <div
        className={css`
          position: absolute;
          top: 80px;
          right: 80px;
          pointer-events: none;
        `}
      >
        {legend}
      </div>
    )}
  </div>
);
